/**
 * useHrCertificates — Admin-Web hooks for Issue #362 (HR credential expiry +
 * login freeze).
 *
 * 红线 (AI GUIDE §6):
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch、不构造假状态。
 *   - 响应中不携带 certificateNoCipher / certificateNoHash；前端只看 last4 + 状态。
 *   - 错误信息走统一 toast 信号，不静默吞掉；接口 409 时提示乐观锁冲突重试。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type HrCertificateDto = ApiComponents['schemas']['HrCertificateDto'];
export type HrCertificateListDto =
  ApiComponents['schemas']['HrCertificateListDto'];
export type HrCertificateEventDto =
  ApiComponents['schemas']['HrCertificateEventDto'];
export type HrCertificateEventListDto =
  ApiComponents['schemas']['HrCertificateEventListDto'];
export type HrCertificateWarningCandidateDto =
  ApiComponents['schemas']['HrCertificateWarningCandidateDto'];
export type HrCertificateWarningCandidateListDto =
  ApiComponents['schemas']['HrCertificateWarningCandidateListDto'];
export type CreateHrCertificateDto =
  ApiComponents['schemas']['CreateHrCertificateDto'];
export type UpdateHrCertificateDto =
  ApiComponents['schemas']['UpdateHrCertificateDto'];
export type ReviewHrCertificateDto =
  ApiComponents['schemas']['ReviewHrCertificateDto'];
export type ArchiveHrCertificateDto =
  ApiComponents['schemas']['ArchiveHrCertificateDto'];
export type AcknowledgeCertificateWarningDto =
  ApiComponents['schemas']['AcknowledgeCertificateWarningDto'];

export interface HrCertificateApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, status?: number): HrCertificateApiError {
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不符，请刷新后重试',
      status,
    };
  }
  if (status === 403) return { message: '没有权限执行此操作', status };
  if (status === 400) {
    return { message: '请求参数不合法，请检查必填项与字段格式', status };
  }
  if (status === 404) return { message: '资源不存在或不可访问', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

export interface ListHrCertificateFilter {
  employeeId?: string;
  userId?: string;
  certificateType?: string;
  status?: HrCertificateDto['status'];
  reviewStatus?: HrCertificateDto['reviewStatus'];
  mandatoryOnly?: boolean;
  loginFreezeActive?: boolean;
  page?: number;
  limit?: number;
}

export function useHrCertificates() {
  const [items, setItems] = useState<HrCertificateDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrCertificateApiError | null>(null);

  const fetchCertificates = useCallback(
    async (filter?: ListHrCertificateFilter) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.employeeId) query.employeeId = filter.employeeId;
        if (filter?.userId) query.userId = filter.userId;
        if (filter?.certificateType) {
          query.certificateType = filter.certificateType;
        }
        if (filter?.status) query.status = filter.status;
        if (filter?.reviewStatus) query.reviewStatus = filter.reviewStatus;
        if (typeof filter?.mandatoryOnly === 'boolean') {
          query.mandatoryOnly = filter.mandatoryOnly;
        }
        if (typeof filter?.loginFreezeActive === 'boolean') {
          query.loginFreezeActive = filter.loginFreezeActive;
        }
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/certificates',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    items,
    total,
    loading,
    error,
    fetchCertificates,
  };
}

export function useHrCertificateDetail() {
  const [item, setItem] = useState<HrCertificateDto | null>(null);
  const [events, setEvents] = useState<HrCertificateEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrCertificateApiError | null>(null);

  const fetchCertificate = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/certificates/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setItem(data);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async (id: string) => {
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/certificates/{id}/events',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setEvents([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    }
  }, []);

  return {
    item,
    events,
    loading,
    error,
    fetchCertificate,
    fetchEvents,
  };
}

export function useHrCertificateMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrCertificateApiError | null>(null);

  const createCertificate = useCallback(
    async (body: CreateHrCertificateDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/certificates',
          { body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateCertificate = useCallback(
    async (id: string, body: UpdateHrCertificateDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PATCH(
          '/api/v1/hr/certificates/{id}',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reviewCertificate = useCallback(
    async (id: string, body: ReviewHrCertificateDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/certificates/{id}/review',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const acknowledgeWarning = useCallback(
    async (id: string, body: AcknowledgeCertificateWarningDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/certificates/{id}/warning-acks',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const archiveCertificate = useCallback(
    async (id: string, body: ArchiveHrCertificateDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/hr/certificates/{id}/archive',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    createCertificate,
    updateCertificate,
    reviewCertificate,
    acknowledgeWarning,
    archiveCertificate,
  };
}

export function useHrCertificateWarnings() {
  const [items, setItems] = useState<HrCertificateWarningCandidateDto[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrCertificateApiError | null>(null);

  const fetchWarnings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/certificates/warnings/candidates',
        {},
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setGeneratedAt(data.generatedAt);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, generatedAt, loading, error, fetchWarnings };
}

export const HR_CERTIFICATE_STATUS_LABELS: Record<
  HrCertificateDto['status'],
  string
> = {
  draft: '草稿',
  active: '在效',
  expiring_soon: '即将到期',
  overdue: '已过期',
  renewed: '已续期',
  archived: '已归档',
};

export const HR_CERTIFICATE_REVIEW_STATUS_LABELS: Record<
  HrCertificateDto['reviewStatus'],
  string
> = {
  pending_review: '待审核',
  approved: '已批准',
  rejected: '已驳回',
  retracted: '已撤回',
};

export const HR_CERTIFICATE_WARNING_BUCKET_LABELS: Record<
  HrCertificateWarningCandidateDto['bucket'],
  string
> = {
  remind_30: '30 天预警',
  remind_7: '7 天预警',
};

export const HR_CERTIFICATE_CRITICAL_ROLE_LABELS: Record<string, string> = {
  doctor: '医生',
  nurse: '护士',
  lab_tech: '实验室技师',
  qa: '质量负责人',
  finance: '财务',
  pharmacy: '药剂',
  other: '其它关键岗',
};

/**
 * Mask the certificate number for safe display. Backend already returns only
 * the last 4 digits; UI defensively prefixes a placeholder so it is obvious
 * the value is intentionally redacted.
 */
export function maskCertificateNo(last4: string | null | undefined): string {
  if (!last4) return '—';
  return `****-${last4}`;
}
